import { FormControl, Grid, MenuItem, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useCallback, useEffect, useState } from "react";
import SelectButton from "../../../../../../components/shortcuts/components/SelectButton";
import { VitalsInputProps } from "../../../../../../components/widgets/modals/encounter";
import CodingSystems from "../../../../../../constants/CodingSystems";
import usePractitioner from "../../../../../../hooks/usePractitioner";
import { bloodGlucoseTypes } from "../../../../../../slices/options/anatomy";
import {
  A1C_DISPLAY,
  calculateBloodGlucoseFeedback,
  RBS_DISPLAY,
} from "../../../../../../utils/bloodGlucoseUtils";
import { createCodeableConcept } from "../../../../../../utils/DataUtils";
import { testRegExp } from "../../../../../../utils/regex";
import { Feedback } from "../types/commonCDSTypes";
import { updateMetaData } from "../VitalsFormHelper";
import CdsArrowTooltip from "./cdsArrowTooltip";
import CdsFeedbackTextField, {
  getCdsTextColor,
} from "./CdsFeedbackTextField";
import { createDefaultVital } from ".";

export default function BloodGlucoseInput({
  value,
  disabled,
  size,
  encounter,
  patient,
  onChange,
}: VitalsInputProps) {
  const theme = useTheme();
  const practitioner = usePractitioner();
  const [glucoseValue, setGlucoseValue] = useState(
    value?.valueQuantity?.value ?? "",
  );
  const [testType, setTestType] = useState(value?.method?.text ?? RBS_DISPLAY);
  const [feedback, setFeedback] = useState<Feedback>();
  const [unit, setUnit] = useState(
    value?.valueQuantity?.unit ?? (testType === A1C_DISPLAY ? "%" : "mg/dL"),
  );

  useEffect(() => {
    if (testType === A1C_DISPLAY) {
      setUnit("%");
    } else if (unit === "%") {
      setUnit("mg/dL");
    }
    if (glucoseValue) {
      handleGlucoseLevelChange(parseFloat(glucoseValue));
    }
  }, [testType, glucoseValue]);

  useEffect(() => {
    if (value?.valueQuantity?.value) {
      setGlucoseValue(value?.valueQuantity?.value);
    }
    if (value?.method?.text) {
      setTestType(value?.method?.text);
    }
  }, [value]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const targetValue = event.target.value ?? "";
      const valid = testRegExp(
        "non-negative decimal number or empty string",
        targetValue,
      );
      if (!valid) return;

      setGlucoseValue(targetValue);
      handleGlucoseLevelChange(parseFloat(targetValue));
      const newVital = {
        ...(value ||
          createDefaultVital(
            encounter,
            patient,
            CodingSystems.OBSERVATION_CODE,
            "2345-7",
            "Blood Glucose Level",
            "%",
          )),
        method: createCodeableConcept(
          CodingSystems.OBSERVATION_METHOD,
          bloodGlucoseTypes.find((c) => c.display.toLowerCase() === testType)
            ?.code,
          testType,
        ),
        valueQuantity: {
          value: targetValue,
          unit,
        },
      };
      updateMetaData(newVital, practitioner);
      onChange(newVital);
    },
    [encounter, patient, unit, practitioner, onChange],
  );

  const getOptions = testType === A1C_DISPLAY ? ["%"] : ["mg/dL", "mmol/L"];

  const bglFeedbackType = glucoseValue ? feedback?.type : undefined;
  const bglUnitSelectSx =
    disabled && bglFeedbackType
      ? {
          color: `${getCdsTextColor(bglFeedbackType, theme.palette)} !important`,
          WebkitTextFillColor: `${getCdsTextColor(
            bglFeedbackType,
            theme.palette,
          )} !important`,
          opacity: 1,
          "& .MuiButton-endIcon": { color: "inherit" },
        }
      : undefined;

  const handleGlucoseLevelChange = useCallback(
    (glucoseValue: number, currentUnit: string = unit) => {
      setFeedback(
        calculateBloodGlucoseFeedback(glucoseValue, testType, currentUnit),
      );
    },
    [testType, unit],
  );

  const handleTestTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newTestType = e.target.value;
      setTestType(newTestType);
      const currentValue = value?.valueQuantity?.value ?? glucoseValue;
      handleGlucoseLevelChange(parseFloat(currentValue));
      const newVital = {
        ...(value ||
          createDefaultVital(
            encounter,
            patient,
            CodingSystems.OBSERVATION_CODE,
            "2345-7",
            "Blood Glucose",
            "%",
          )),
        method: createCodeableConcept(
          CodingSystems.OBSERVATION_METHOD,
          bloodGlucoseTypes.find((c) => c.display.toLowerCase() === newTestType)
            ?.code,
          newTestType,
        ),
      };
      updateMetaData(newVital, practitioner);
      onChange(newVital);
    },
    [value, glucoseValue, handleGlucoseLevelChange, practitioner, onChange],
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <FormControl fullWidth>
          <TextField
            id="vitals-blood-glucose-test"
            select
            label="BG - Test Type"
            value={testType}
            disabled={disabled}
            onChange={handleTestTypeChange}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
          >
            {bloodGlucoseTypes.map((c) => (
              <MenuItem value={c.display.toLowerCase()} key={c.code}>
                {c.display}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <FormControl fullWidth>
          <CdsArrowTooltip
            type={feedback?.type}
            feedbackText={feedback?.message}
            openCDS={parseInt(value?.valueQuantity?.value, 10) >= 1}
          >
            <CdsFeedbackTextField
              feedbackType={glucoseValue ? feedback?.type : undefined}
              id="vitals-blood-glucose"
              value={glucoseValue}
              disabled={disabled}
              label="BGL"
              inputMode="numeric"
              autoComplete="off"
              InputProps={{
                endAdornment: (
                  <SelectButton
                    label={unit}
                    options={getOptions}
                    variant="text"
                    disabled={disabled}
                    sx={bglUnitSelectSx}
                    onSelect={(newUnit) => {
                      setUnit(newUnit);
                      const newVital = {
                        ...(value ||
                          createDefaultVital(
                            encounter,
                            patient,
                            CodingSystems.OBSERVATION_CODE,
                            "2345-7",
                            "Blood Glucose",
                            "%",
                          )),
                        valueQuantity: {
                          value: glucoseValue,
                          unit: newUnit,
                        },
                      };
                      updateMetaData(newVital, practitioner);
                      handleGlucoseLevelChange(
                        parseFloat(glucoseValue),
                        newUnit,
                      );
                      onChange(newVital);
                    }}
                    size={size}
                    getOptionLabel={(value) => value}
                  />
                ),
              }}
              size={size}
              variant="outlined"
              type="text"
              onChange={handleChange}
            />
          </CdsArrowTooltip>
        </FormControl>
      </Grid>
    </Grid>
  );
}
